import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { isForbiddenError, toastErrorIfNotForbidden } from "@/shared/utils/errorHandling";
import { SUPPLIERS_MESSAGES } from "../configs/constants";
import { SUPPLIERS_QUERY_KEYS } from "../configs/queryKeys";
import { SupplierService } from "../services/suppliers.service";
import type {
  CreateSupplierPayload,
  UpdateSupplierPayload,
} from "../services/suppliers.service.types";

export const useSuppliersMutations = () => {
  const queryClient = useQueryClient();

  const createSupplierMutation = useMutation({
    mutationFn: (payload: CreateSupplierPayload) =>
      SupplierService.createSupplier(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEYS.all });
      toast.success(SUPPLIERS_MESSAGES.CREATE_SUCCESS);
    },
    onError: (error) => {
      toastErrorIfNotForbidden(error, SUPPLIERS_MESSAGES.CREATE_ERROR);
    },
  });

  const updateSupplierMutation = useMutation({
    mutationFn: (payload: UpdateSupplierPayload) =>
      SupplierService.updateSupplier(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEYS.all });
      toast.success(SUPPLIERS_MESSAGES.UPDATE_SUCCESS);
    },
    onError: (error) => {
      toastErrorIfNotForbidden(error, SUPPLIERS_MESSAGES.UPDATE_ERROR);
    },
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: (id: string) => SupplierService.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEYS.all });
      toast.success(SUPPLIERS_MESSAGES.DELETE_SUCCESS);
    },
    onError: (error) => {
      if (isForbiddenError(error)) return;
      if (error instanceof AxiosError && error.response?.status === 422) {
        toast.error(SUPPLIERS_MESSAGES.DELETE_ERROR, { description: SUPPLIERS_MESSAGES.DELETE_ERROR_LINKED_TO_ACCOUNTING });
        return;
      }
      toast.error(SUPPLIERS_MESSAGES.DELETE_ERROR);
    },
  });

  const deleteSuppliersMultipleMutation = useMutation({
    mutationFn: (ids: string[]) => SupplierService.deleteSuppliers(ids),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEYS.all });
      toast.success(SUPPLIERS_MESSAGES.BULK_DELETE_SUCCESS(ids.length));
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : SUPPLIERS_MESSAGES.BULK_DELETE_ERROR;
      toastErrorIfNotForbidden(error, message);
    },
  });

  return {
    createSupplierMutation,
    updateSupplierMutation,
    deleteSupplierMutation,
    deleteSuppliersMultipleMutation,
  };
};
